<template>
  <component
    :is="modalComponent"
    background-color="low"
    class="mood-warm"
    :name="CHAT_HISTORY_DIALOG_NAME"
    :title="i18n(translations.chatHistoryTitle)"
  >
    <template #body>
      <RevList :has-external-borders="false" hide-dividers variant="compact">
        <RevListItemStatic
          class="items-center cursor-pointer"
          @click="handleNewChatClick"
        >
          <template #label>
            {{ i18n(translations.chatHistoryNewChat) }}
          </template>

          <template #suffix>
            <RevButtonIcon :icon="IconNewConversationFilled" variant="ghost" />
          </template>
        </RevListItemStatic>
        <template v-if="pending">
          <RevListItemStatic
            v-for="i in 3"
            :key="`skeleton-${i}`"
            class="items-center"
          >
            <template #label>
              <RevSkeleton
                class="mb-12"
                height="12px"
                shape="line"
                width="140px"
              />
            </template>
            <template #description>
              <RevSkeleton height="12px" shape="line" width="66px" />
            </template>
          </RevListItemStatic>
        </template>
        <RevListItemStatic
          v-for="chat in list"
          v-else
          :key="chat.chatId"
          class="items-center cursor-pointer"
          @click="handleOpenChat(chat.chatId)"
        >
          <template #label>
            {{ chat.title }}
          </template>
          <template #description>
            {{ formatRelativeDate(chat.createdAt, currentLocale) }}
          </template>
          <template #suffix>
            <RevButtonIcon :icon="IconChevronRight" variant="ghost" />
          </template>
        </RevListItemStatic>
      </RevList>
    </template>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { formatRelativeDate } from '@backmarket/nuxt-layer-cms/DynamicTextDates.utils'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevDialog } from '@ds/components/Dialog'
import { RevDrawer } from '@ds/components/Drawer'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { closeModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconNewConversationFilled } from '@ds/icons/IconNewConversationFilled'

import { useFetchShoppingAssistantChatHistory } from '~/scopes/shopping-assistant/composables/useFetchShoppingAssistantChatHistory'
import { useNewConversationDialog } from '~/scopes/shopping-assistant/composables/useNewConversationDialog'

import { CHAT_HISTORY_DIALOG_NAME } from '../../constants'
import translations from '../ShoppingAssistant.translations'

const TRACKING_ZONE = 'ai_shopping_assistant_chat_history'

const emit = defineEmits<{
  (e: 'new-chat'): void
  (e: 'open-chat', chatId: string): void
}>()

const i18n = useI18n()
const currentLocale = useI18nLocale()
const breakpoint = useClientOnlyComputedBreakpoint()
const tracking = useTracking()

const { data, pending } = useFetchShoppingAssistantChatHistory()
const { requestNewChat } = useNewConversationDialog()

const list = computed(() => data.value?.results ?? [])

const modalComponent = computed(() =>
  breakpoint.value > Breakpoint.SM ? RevDrawer : RevDialog,
)

function handleNewChatClick() {
  requestNewChat(() => {
    closeModal(CHAT_HISTORY_DIALOG_NAME)
    emit('new-chat')
    tracking.trackClick({ name: 'new_chat', zone: TRACKING_ZONE })
  })
}

function handleOpenChat(chatId: string) {
  closeModal(CHAT_HISTORY_DIALOG_NAME)
  emit('open-chat', chatId)
  tracking.trackClick({ name: 'open_chat', zone: TRACKING_ZONE, chatId })
}
</script>
